import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { Path } from 'react-hook-form';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import {
	type W_BillingCode_UpdateBillingCode_RM,
} from 'ab-api/web/billingCode/editBillingCodes';
import type { W_BillingCode_FindForJob_VM_Single } from 'ab-api/web/billingCode/findForJob';

import { filterMap } from '@acceligentllc/shared/utils/array';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { CompoundUnitEnum, QuantityUnitLabel } from '@acceligentllc/shared/enums/quantityUnit';

import { BillingCodeEditMode } from 'ab-enums/billingCodeEditMode.enum';

import { Button } from '@acceligentllc/storybook';

import ColorSquare from 'af-components/ColorSquare';
import ConfirmationModal from 'af-components/ConfirmationModal';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import CustomModal from 'af-components/CustomModal';
import type { SimpleTableRow } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import SimpleTableRHFWrapper from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import { setOneErrorToRHF, validateFieldArray, validateOneField } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper/validationRHF';
import type { TableRef } from 'af-components/RHFTableCellComponents/SimpleTable';
import type { FooterButton } from 'af-components/RHFTableCellComponents/SimpleTable/types';
import DropdownCellRenderer from 'af-components/RHFTableCellRenderers/DropdownCellRenderer';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';
import TextAreaCellRenderer from 'af-components/RHFTableCellRenderers/TextAreaCellRenderer';
import Tooltip from 'af-components/Tooltip';

import * as BillingCodeActions from 'af-actions/billingCode/index';

import BillingCodeArchiveModal from './BillingCodeArchiveModal';
import BillingCodeEditModeModal from './BillingCodeEditModeModal';
import styles from './styles.module.scss';

interface OwnProps {
	showModal: boolean;
	closeModal: () => void;
	workRequestId: number;
	billingCodes: W_BillingCode_FindForJob_VM_Single[];
	onSubmit: (billingCodes: W_BillingCode_UpdateBillingCode_RM[]) => void;
	errors: Nullable<BackendBillingCodeErrors>;
}

type Props = ConnectedProps<typeof connector> & OwnProps;

export interface BillingCodeFM {
	id: number | string;
	databaseId: Nullable<number>;
	deletedAt: Nullable<Date>;
	createdAt: Date;
	editMode: Nullable<BillingCodeEditMode>;
	lineItemNumber: Nullable<number>;
	customerNumber: Nullable<number>;
	customerId: string;
	ownerNumber: Nullable<string>;
	ownerId: Nullable<string>;
	unit: Nullable<QuantityUnitType>;
	unitPrice: string;
	bidQuantity: Nullable<string>;
	groupId: Nullable<number>;
	group: Nullable<string>;
	groupColor: Nullable<ColorPalette>;
	description: string;
}

export type BillingCodeColumn = keyof BillingCodeFM;
export type BillingCodeColumnValueTypes = BillingCodeFM[keyof BillingCodeFM];
export type BillingCodeFieldPath = `billingCodes.${number}.${BillingCodeColumn}`;

interface FormValues {
	billingCodes: BillingCodeFM[];
}

interface DropdownOption<T> {
	id: T;
	label: string;
}

interface GroupOption extends DropdownOption<number> {
	color: Nullable<ColorPalette>;
}

const compoundKeys = Object.keys(CompoundUnitEnum);
const quantityUnitOptions: DropdownOption<QuantityUnitType>[] = filterMap(
	Object.keys(QuantityUnitLabel),
	(_unit: QuantityUnitType) => !compoundKeys.includes(_unit),
	(_unit: QuantityUnitType) => ({ id: _unit, label: QuantityUnitLabel[_unit] })
);

const billingCodeFieldKeys = [
	'id',
	'databaseId',
	'deletedAt',
	'createdAt',
	'editMode',
	'lineItemNumber',
	'customerNumber',
	'customerId',
	'ownerNumber',
	'ownerId',
	'unit',
	'unitPrice',
	'bidQuantity',
	'groupId',
	'group',
	'groupColor',
	'description',
];

function isBillingCodeField(field: string): field is keyof BillingCodeFM {
	return billingCodeFieldKeys.includes(field as keyof BillingCodeFM);
}

function isExistingBillingCode(row: BillingCodeFM): boolean {
	return !!row.databaseId;
}

function isBillingCodeEditable(row: BillingCodeFM): boolean {
	if (row.deletedAt) {
		return false;
	}

	if (!isExistingBillingCode(row)) {
		return true;
	}

	return !!row.editMode;
}

function shouldPromptForBillingCodeEditMode(row: BillingCodeFM): boolean {
	if (row.deletedAt) {
		return false;
	}

	if (!isExistingBillingCode(row)) {
		return false;
	}

	return !row.editMode;
}

const renderEditModeTooltip = (editMode: BillingCodeEditMode) => {
	if (editMode === BillingCodeEditMode.DIRECT_EDIT) {
		return (
			<div>
				<strong>Edit & recalculate all history</strong>
				<div>Fixes a wrong price or typo. The corrected value is applied retroactively.</div>
			</div>
		);
	}

	if (editMode === BillingCodeEditMode.ARCHIVE_AND_EDIT) {
		return (
			<div>
				<strong>Archive and create new</strong>
				<div>Current code is archived. New version takes effect from the date you choose. Best for yearly contract renewals.</div>
			</div>
		);
	}

	return null;
};

export const fieldNameMapper: Record<BillingCodeColumn, string> = {
	id: 'ID',
	databaseId: 'Database ID',
	deletedAt: 'Deleted At',
	createdAt: 'Created At',
	editMode: 'Edit Mode',
	lineItemNumber: 'Line Item Number',
	customerNumber: 'Customer Number',
	customerId: 'Customer ID',
	ownerNumber: 'Owner Number',
	ownerId: 'Owner ID',
	unit: 'Unit',
	unitPrice: 'Unit Price',
	bidQuantity: 'Bid Quantity',
	groupId: 'Group ID',
	group: 'Group',
	groupColor: 'Group Color',
	description: 'Description',
};

export const uniqueFields: BillingCodeColumn[] = ['lineItemNumber', 'customerId'];
export const requiredFields: BillingCodeColumn[] = ['lineItemNumber', 'customerId', 'unit', 'unitPrice', 'description'];
export const numberFields: BillingCodeColumn[] = ['lineItemNumber', 'customerNumber', 'ownerNumber', 'unitPrice', 'bidQuantity'];

export type BillingCodeFieldErrors = {
	[K in keyof BillingCodeFM]?: string;
};

type BillingCodeErrors = Record<string, BillingCodeFieldErrors>;

export type BackendBillingCodeErrors = {
	billingCodes?: BillingCodeErrors;
};

const EditBillingCodesModal: React.FC<Props> = (props) => {
	const {
		closeModal,
		showModal,
		billingCodes,
		onSubmit,
		errors,
		getGroups,
		workRequestId,
	} = props;

	const [showDiscardModalData, setShowDiscardModalData] = React.useState(false);
	const [groupOptions, setGroupOptions] = React.useState<GroupOption[]>([]);
	const [rowPendingEditMode, setRowPendingEditMode] = React.useState<Nullable<number>>(null);
	const [rowPendingArchive, setRowPendingArchive] = React.useState<Nullable<number>>(null);

	const tableRef = React.useRef<TableRef>(null);

	const initialBillingCodes = React.useMemo<BillingCodeFM[]>(() => {
		return billingCodes?.map((billingCode) => ({
			databaseId: billingCode.id,
			id: billingCode.id,
			deletedAt: billingCode.deletedAt,
			createdAt: billingCode.createdAt,
			editMode: null,
			lineItemNumber: billingCode.lineItemNumber,
			customerNumber: billingCode.customerNumber,
			customerId: billingCode.customerId,
			ownerNumber: billingCode.ownerNumber,
			ownerId: billingCode.ownerId,
			unit: billingCode.unit,
			unitPrice: billingCode.unitPrice,
			bidQuantity: billingCode.bidQuantity,
			groupId: billingCode.groupId,
			group: billingCode.groupName,
			groupColor: billingCode.groupColor,
			description: billingCode.description,
		})) ?? [];
	}, [billingCodes]);

	const originalBillingCodesByDatabaseId = React.useMemo(() => {
		return initialBillingCodes.reduce<Record<number, BillingCodeFM>>((acc, billingCode) => {
			if (billingCode.databaseId) {
				acc[billingCode.databaseId] = billingCode;
			}

			return acc;
		}, {});
	}, [initialBillingCodes]);

	const {
		control,
		setError,
		clearErrors,
		getValues,
		formState,
		unregister,
		setValue,
	} = useForm<FormValues>({
		values: {
			billingCodes: initialBillingCodes,
		},
	});

	const billingCodesWatch = useWatch({ control, name: 'billingCodes' });

	const {
		fields,
		append,
		remove,
	} = useFieldArray({
		control,
		name: 'billingCodes',
	});

	const openEditModeModal = React.useCallback((rowIndex: number) => () => {
		setRowPendingEditMode(rowIndex);
	}, []);

	const closeEditModeModal = React.useCallback(() => {
		setRowPendingEditMode(null);
	}, []);

	const closeArchiveModal = React.useCallback(() => {
		setRowPendingArchive(null);
	}, []);

	const validateBillingCodesArray = React.useCallback(() => {
		const valuesForParsing = getValues();

		clearErrors();

		validateFieldArray(valuesForParsing, setError, {
			requiredFields,
			numberFields,
			uniqueFields,
			fieldNameMapper,
			arrayKey: 'billingCodes',
			shouldValidateRow: (billingCode) => !billingCode.deletedAt,
		});
	}, [clearErrors, getValues, setError]);

	const validateField = React.useCallback((
		cellName: BillingCodeFieldPath,
		fieldName: BillingCodeColumn,
		value: BillingCodeColumnValueTypes
	) => {
		clearErrors(cellName);

		validateOneField({
			cellName,
			fieldName,
			value,
			setError,
			config: {
				requiredFields,
				numberFields,
				fieldNameMapper,
				arrayKey: 'billingCodes',
			},
		});
	}, [clearErrors, setError]);

	React.useEffect(() => {
		validateBillingCodesArray();
	}, [validateBillingCodesArray]);

	const findGroups = React.useCallback(async () => {
		const groups = await getGroups(workRequestId);
		setGroupOptions(groups.map((group) => ({
			id: group.id,
			label: group.name,
			color: group.color,
		})));
	}, [getGroups, workRequestId]);

	React.useEffect(() => {
		findGroups();
	}, [findGroups]);

	// Set errors from backend response after trying to save the form.
	React.useEffect(() => {
		if (errors?.billingCodes) {
			Object.entries(errors.billingCodes).forEach(([index, _fields]) => {
				Object.entries(_fields).forEach(([field, message]) => {
					const path = `billingCodes.${index}.${field}` as Path<FormValues>;
					setOneErrorToRHF(path, message, setError);
				});
			});
		} else {
			clearErrors();
		}
	}, [clearErrors, errors, setError]);

	const archiveBillingCodeRow = React.useCallback((index?: number | number[]) => {
		if (index === undefined || Array.isArray(index)) {
			remove(index);
			return;
		}

		const row = getValues(`billingCodes.${index}`);

		if (!row) {
			return;
		}

		// New unsaved row can be removed completely.
		if (!row.databaseId) {
			remove(index);
			return;
		}

		const originalBillingCode = originalBillingCodesByDatabaseId[row.databaseId];

		if (!originalBillingCode) {
			return;
		}

		setValue(`billingCodes.${index}`, {
			...originalBillingCode,
			deletedAt: new Date(),
			editMode: null,
		}, {
			shouldDirty: true,
			shouldTouch: true,
		});

		clearErrors(`billingCodes.${index}` as Path<FormValues>);
		validateBillingCodesArray();
	}, [
		clearErrors,
		getValues,
		originalBillingCodesByDatabaseId,
		remove,
		setValue,
		validateBillingCodesArray,
	]);

	const confirmArchiveBillingCode = React.useCallback(() => {
		if (rowPendingArchive === null) {
			return;
		}

		archiveBillingCodeRow(rowPendingArchive);
		closeArchiveModal();
	}, [archiveBillingCodeRow, closeArchiveModal, rowPendingArchive]);

	const promptEditModeIfNeeded = React.useCallback((row: BillingCodeFM & SimpleTableRow) => {
		if (!shouldPromptForBillingCodeEditMode(row)) {
			return false;
		}

		setRowPendingEditMode(row.index);
		return true;
	}, []);

	const promptArchiveBillingCodeRow = React.useCallback((index?: number | number[]) => {
		if (index === undefined || Array.isArray(index)) {
			archiveBillingCodeRow(index);
			return;
		}

		const row = getValues(`billingCodes.${index}`);

		if (!row) {
			return;
		}

		// New unsaved row should still be removed immediately.
		if (!row.databaseId) {
			archiveBillingCodeRow(index);
			return;
		}

		setRowPendingArchive(index);
	}, [archiveBillingCodeRow, getValues]);

	const selectedBillingCodeForArchive = React.useMemo(() => {
		if (rowPendingArchive === null) {
			return null;
		}

		const row = getValues(`billingCodes.${rowPendingArchive}`);

		if (!row) {
			return null;
		}

		if (row.databaseId) {
			return originalBillingCodesByDatabaseId[row.databaseId] ?? row;
		}

		return row;
	}, [getValues, originalBillingCodesByDatabaseId, rowPendingArchive]);

	const onSubmitBillingCodes = React.useCallback(() => {
		const values = getValues();
		const billingCodesForSaving: W_BillingCode_UpdateBillingCode_RM[] = values.billingCodes.map((field) => {
			if (field.unit === null) {
				throw new Error('Unit should be set at this point'); // Should never happen
			}

			if (field.lineItemNumber === null) {
				throw new Error('Line item number should be set at this point'); // Should never happen
			}

			return {
				...(field.databaseId !== null ? { id: field.databaseId } : {}),
				deletedAt: field.deletedAt,
				editMode: field.editMode,
				lineItemNumber: +field.lineItemNumber,
				customerNumber: field.customerNumber ? +field.customerNumber : null,
				customerId: field.customerId,
				ownerNumber: field.ownerNumber ?? null,
				ownerId: field.ownerId ?? null,
				unit: field.unit,
				unitPrice: field.unitPrice,
				bidQuantity: field.bidQuantity ?? null,
				groupId: field.groupId ?? null,
				description: field.description,
			};
		});

		onSubmit(billingCodesForSaving);
	}, [getValues, onSubmit]);

	const filterGroups = React.useCallback((option: DropdownOption<number>, searchText: string) => {
		return option.label.toLowerCase().includes(searchText);
	}, []);

	const filterUnits = React.useCallback((option: DropdownOption<QuantityUnitType>, searchText: string) => {
		return option.label.toLowerCase().includes(searchText);
	}, []);

	const validateOnChange = React.useCallback((cellName: BillingCodeFieldPath) => {
		const splitName = cellName.split('.');

		// Expecting names like: ['billingCodes', '3', 'lineItemNumber'].
		if (splitName[0] !== 'billingCodes' || !Number.isInteger(+splitName[1]) || !isBillingCodeField(splitName[2])) {
			return;
		}

		const fieldName = splitName[2];
		const value = getValues(cellName);

		if (uniqueFields.includes(fieldName)) {
			validateBillingCodesArray();
			return;
		}

		validateField(cellName, fieldName, value);
	}, [getValues, validateBillingCodesArray, validateField]);

	const onBillingCodeCellChange = React.useCallback((cellName: BillingCodeFieldPath) => {
		const splitName = cellName.split('.');

		if (splitName[0] !== 'billingCodes' || !Number.isInteger(+splitName[1]) || !isBillingCodeField(splitName[2])) {
			return;
		}

		const rowIndex = +splitName[1];
		const fieldName = splitName[2];

		if (fieldName === 'groupId') {
			const groupId = getValues(cellName);
			const group = groupOptions.find((option) => option.id === groupId);

			setValue(`billingCodes.${rowIndex}.group`, group?.label ?? null, {
				shouldDirty: true,
				shouldTouch: true,
			});
			setValue(`billingCodes.${rowIndex}.groupColor`, group?.color ?? null, {
				shouldDirty: true,
				shouldTouch: true,
			});
		}

		validateOnChange(cellName);
	}, [getValues, groupOptions, setValue, validateOnChange]);

	const renderGroup = React.useCallback((option: GroupOption) => {
		return <div className={styles['group-option']}>{option.color && <ColorSquare color={option.color} />}{option.label}</div>;
	}, []);

	const onClearGroup = React.useCallback((rowIndex: number) => () => {
		setValue(`billingCodes.${rowIndex}.groupId`, null, { shouldDirty: true, shouldTouch: true });
		setValue(`billingCodes.${rowIndex}.group`, null, { shouldDirty: true, shouldTouch: true });
		setValue(`billingCodes.${rowIndex}.groupColor`, null, { shouldDirty: true, shouldTouch: true });

		validateOnChange(`billingCodes.${rowIndex}.groupId`);
	}, [setValue, validateOnChange]);

	const renderReadonlyValue = React.useCallback((
		value: Nullable<string | number>,
		cellType: 'text' | 'numeric' | 'dollar' | 'quantity',
		isDeleted: boolean,
		onClick?: () => void
	) => {
		return (
			<div
				className={`${styles[`readonly-cell--${cellType}`]} ${isDeleted ? styles['readonly-cell--archived'] : ''} ${onClick ? styles['readonly-cell--clickable'] : ''}`}
				onClick={onClick}
			>
				<div>{cellType === 'dollar' && '$'}</div>
				<span>{value ?? ''}</span>
			</div>
		);
	}, []);

	const renderReadonlyGroup = React.useCallback((row: BillingCodeFM & SimpleTableRow, onClick?: () => void) => {
		const className = `${styles['readonly-cell']} ${row.deletedAt ? styles['readonly-cell--archived'] : ''} ${onClick ? styles['readonly-cell--clickable'] : ''}`;

		return (
			<span className={className} onClick={onClick}>
				{row.groupColor && <ColorSquare color={row.groupColor} />}
				{row.group ?? ''}
			</span>
		);
	}, []);

	const renderGroupDropdownCell = React.useCallback((cell: CellContext<BillingCodeFM & SimpleTableRow, string>) => {
		const original = cell.row.original;

		if (!isBillingCodeEditable(original)) {
			const onClick = shouldPromptForBillingCodeEditMode(original)
				? () => promptEditModeIfNeeded(original)
				: undefined;

			return renderReadonlyGroup(original, onClick);
		}

		return (
			<DropdownCellRenderer
				control={control}
				fieldsLength={fields.length}
				filterable={true}
				filterBy={filterGroups}
				getValues={getValues}
				id={`${original.id}`}
				labelKey="label"
				name={`billingCodes.${original.index}.groupId`}
				onChange={onBillingCodeCellChange}
				onClear={onClearGroup(original.index)}
				options={groupOptions}
				placeholder="Select Group"
				renderMenuItem={renderGroup}
				rhfId={original.id}
				unregister={unregister}
				valueKey="id"
				withCaret={true}
			/>
		);
	}, [
		control,
		fields.length,
		filterGroups,
		getValues,
		groupOptions,
		onBillingCodeCellChange,
		onClearGroup,
		promptEditModeIfNeeded,
		renderGroup,
		renderReadonlyGroup,
		unregister,
	]);

	const renderUnitDropdownCell = React.useCallback((cell: CellContext<BillingCodeFM & SimpleTableRow, string>) => {
		const original = cell.row.original;
		const isDirectEditMode = original.editMode === BillingCodeEditMode.DIRECT_EDIT;

		if (!isBillingCodeEditable(original) || isDirectEditMode) {
			const onClick = !isDirectEditMode && shouldPromptForBillingCodeEditMode(original)
				? () => promptEditModeIfNeeded(original)
				: undefined;
			const value = cell.getValue();
			const label = value ? QuantityUnitLabel[value as QuantityUnitType] ?? value : '';

			return renderReadonlyValue(label, 'text', !!original.deletedAt, onClick);
		}

		return (
			<DropdownCellRenderer
				control={control}
				fieldsLength={fields.length}
				filterable={true}
				filterBy={filterUnits}
				getValues={getValues}
				id={`${original.id}`}
				labelKey="label"
				name={`billingCodes.${original.index}.unit`}
				onChange={onBillingCodeCellChange}
				options={quantityUnitOptions}
				placeholder="Select Unit"
				rhfId={original.id}
				unregister={unregister}
				valueKey="id"
				withCaret={true}
			/>
		);
	}, [
		control,
		fields.length,
		filterUnits,
		getValues,
		onBillingCodeCellChange,
		promptEditModeIfNeeded,
		renderReadonlyValue,
		unregister,
	]);

	const renderInputCell = React.useCallback((
		cellName: BillingCodeColumn,
		cellType: 'text' | 'numeric' | 'dollar' | 'quantity'
	) => (cell: CellContext<BillingCodeFM & SimpleTableRow, string>) => {
		const original = cell.row.original;

		if (!isBillingCodeEditable(original)) {
			const onClick = shouldPromptForBillingCodeEditMode(original)
				? () => promptEditModeIfNeeded(original)
				: undefined;

			return renderReadonlyValue(cell.getValue(), cellType, !!original.deletedAt, onClick);
		}

		return (
			<InputCellRenderer
				control={control}
				defaultValue={cell.getValue() ?? ''}
				fieldsLength={fields.length}
				getValues={getValues}
				name={`billingCodes.${original.index}.${cellName}`}
				onChange={onBillingCodeCellChange}
				rhfId={original.id}
				type={cellType}
				unregister={unregister}
			/>
		);
	}, [
		control,
		fields.length,
		getValues,
		onBillingCodeCellChange,
		promptEditModeIfNeeded,
		renderReadonlyValue,
		unregister,
	]);

	const renderTextAreaCell = React.useCallback((
		cellName: BillingCodeColumn
	) => (cell: CellContext<BillingCodeFM & SimpleTableRow, string>) => {
		const original = cell.row.original;

		if (!isBillingCodeEditable(original)) {
			const onClick = shouldPromptForBillingCodeEditMode(original)
				? () => promptEditModeIfNeeded(original)
				: undefined;

			return renderReadonlyValue(cell.getValue(), 'text', !!original.deletedAt, onClick);
		}

		return (
			<TextAreaCellRenderer
				control={control}
				defaultValue={cell.getValue() ?? ''}
				fieldsLength={fields.length}
				getValues={getValues}
				name={`billingCodes.${original.index}.${cellName}`}
				onChange={onBillingCodeCellChange}
				rhfId={original.id}
				unregister={unregister}
			/>
		);
	}, [
		control,
		fields.length,
		getValues,
		onBillingCodeCellChange,
		promptEditModeIfNeeded,
		renderReadonlyValue,
		unregister,
	]);

	const renderEditModeCell = React.useCallback((cell: CellContext<BillingCodeFM & SimpleTableRow, unknown>) => {
		const row = cell.row.original;

		if (row.deletedAt || !row.databaseId || !row.editMode) {
			return <div className={row.deletedAt ? styles['readonly-cell--archived'] : styles['readonly-cell']} />;
		}

		return (
			<Tooltip message={renderEditModeTooltip(row.editMode)}>
				<span
					className={styles['info-icon']}
					onClick={openEditModeModal(row.index)}
				>
					<span className="icon-info" />
				</span>
			</Tooltip>
		);
	}, [openEditModeModal]);

	const columns: SimpleTableProps<BillingCodeFM & SimpleTableRow>['columns'] = React.useMemo(() => [
		{
			id: 'lineItemNumber',
			accessor: 'lineItemNumber',
			header: 'Line item*',
			cell: renderInputCell('lineItemNumber', 'numeric'),
			enableSorting: true,
		},
		{
			id: 'customerNumber',
			accessor: 'customerNumber',
			header: 'Customer No.',
			cell: renderInputCell('customerNumber', 'numeric'),
			enableSorting: true,
		},
		{
			id: 'customerId',
			accessor: 'customerId',
			header: 'Customer ID*',
			cell: renderInputCell('customerId', 'text'),
			enableSorting: true,
		},
		{
			id: 'ownerNumber',
			accessor: 'ownerNumber',
			header: 'Owner Number',
			cell: renderInputCell('ownerNumber', 'numeric'),
			enableSorting: true,
		},
		{
			id: 'ownerId',
			accessor: 'ownerId',
			header: 'Owner Id',
			cell: renderInputCell('ownerId', 'text'),
			enableSorting: true,
		},
		{
			id: 'bidQuantity',
			accessor: 'bidQuantity',
			header: 'Bid Qty',
			cell: renderInputCell('bidQuantity', 'quantity'),
			enableSorting: true,
		},
		{
			id: 'unit',
			accessor: 'unit',
			header: 'Unit*',
			cell: renderUnitDropdownCell,
			enableSorting: true,
			size: '120px',
		},
		{
			id: 'unitPrice',
			accessor: 'unitPrice',
			header: 'Unit Price*',
			cell: renderInputCell('unitPrice', 'dollar'),
			enableSorting: true,
			size: '2fr',
		},
		{
			id: 'groupId',
			accessor: 'groupId',
			header: 'Group',
			cell: renderGroupDropdownCell,
			enableSorting: true,
			size: '4fr',
		},
		{
			id: 'description',
			accessor: 'description',
			header: 'Description*',
			cell: renderTextAreaCell('description'),
			enableSorting: true,
			size: '5fr',
		},
		{
			id: 'edit',
			isDisplayColumn: true,
			header: null,
			cell: renderEditModeCell,
			size: 32,
		},
	], [renderEditModeCell, renderGroupDropdownCell, renderInputCell, renderTextAreaCell, renderUnitDropdownCell]);

	const addNewBillingCode = React.useCallback(async () => {
		append({
			id: '', // This id will be generated by RHF.
			databaseId: null,
			deletedAt: null,
			createdAt: new Date(),
			editMode: null,
			lineItemNumber: null,
			customerNumber: null,
			customerId: '',
			ownerNumber: null,
			ownerId: null,
			unit: null,
			unitPrice: '',
			bidQuantity: null,
			groupId: null,
			group: null,
			groupColor: null,
			description: '',
		});

		validateBillingCodesArray();

		requestAnimationFrame(() => {
			// Wait until React commits updates and then scroll to the new row.
			tableRef.current?.scrollToBottomRow();
		});
	}, [append, validateBillingCodesArray]);

	const onDeleteBillingCode = React.useCallback(async () => {
		validateBillingCodesArray();
	}, [validateBillingCodesArray]);

	const canDeleteRow = React.useCallback((row: BillingCodeFM & SimpleTableRow) => {
		return !row.deletedAt;
	}, []);

	const footerButtonsLeft = React.useMemo(() => {
		const resolvedFooterButtons = [{
			iconName: 'ActionPlusSmall',
			label: 'Add Billing Code',
			onClick: addNewBillingCode,
		}];

		return resolvedFooterButtons as FooterButton[];
	}, [addNewBillingCode]);

	const selectedBillingCodeForEditMode = React.useMemo(() => {
		if (rowPendingEditMode === null) {
			return null;
		}

		return getValues(`billingCodes.${rowPendingEditMode}`) ?? null;
	}, [getValues, rowPendingEditMode]);

	const submitEditMode = React.useCallback((mode: BillingCodeEditMode) => {
		if (rowPendingEditMode === null) {
			return;
		}

		const row = getValues(`billingCodes.${rowPendingEditMode}`);

		if (!row?.databaseId) {
			closeEditModeModal();
			return;
		}

		const originalBillingCode = originalBillingCodesByDatabaseId[row.databaseId];

		if (mode === BillingCodeEditMode.DIRECT_EDIT && originalBillingCode) {
			setValue(`billingCodes.${rowPendingEditMode}.unit`, originalBillingCode.unit, {
				shouldDirty: true,
				shouldTouch: true,
			});

			clearErrors(`billingCodes.${rowPendingEditMode}.unit` as Path<FormValues>);
		}

		setValue(`billingCodes.${rowPendingEditMode}.editMode`, mode, {
			shouldDirty: true,
			shouldTouch: true,
		});

		closeEditModeModal();
	}, [
		clearErrors,
		closeEditModeModal,
		getValues,
		originalBillingCodesByDatabaseId,
		rowPendingEditMode,
		setValue,
	]);

	const rowClassName = React.useCallback((row: Row<BillingCodeFM & SimpleTableRow>) => {
		return row.original.deletedAt ? styles['archived-row'] : '';
	}, []);

	const fieldsForTable = React.useMemo(() => {
		return fields.map((field, index) => {
			const watchedRow = billingCodesWatch?.[index] ?? field;
			const rhfId = field.id;

			return {
				...field,
				...watchedRow,
				id: rhfId,
			};
		});
	}, [billingCodesWatch, fields]);

	const closeDiscardModal = React.useCallback(() => setShowDiscardModalData(false), []);

	const discardAndExit = React.useCallback(() => {
		setShowDiscardModalData(false);
		setRowPendingEditMode(null);
		setRowPendingArchive(null);
		closeModal();
	}, [closeModal]);

	const promptCancel = React.useCallback(() => {
		if (!fields) {
			return;
		}

		if (formState.isDirty) {
			setShowDiscardModalData(true);
		} else {
			discardAndExit();
		}
	}, [discardAndExit, fields, formState.isDirty]);

	return (
		<>
			<CustomModal
				className={styles['modal-footer-override']}
				closeModal={promptCancel}
				modalStyle="info"
				showModal={showModal && !showDiscardModalData && rowPendingEditMode === null && rowPendingArchive === null}
				size="xxxl"
			>
				<CustomModal.Header
					closeModal={promptCancel}
					title="Billing Codes"
				/>
				<CustomModal.Body>
					<div className={styles['editable-notice']}>
						To edit an existing billing code, click inside any cell and choose how the change should be applied.
					</div>
					<SimpleTableRHFWrapper<
						BillingCodeFM & SimpleTableRow,
						FormValues,
						'billingCodes'
					>
						canDeleteRow={canDeleteRow}
						columns={columns}
						fields={fieldsForTable}
						footerButtonsLeft={footerButtonsLeft}
						formAccessorName="billingCodes"
						iconName="Archive"
						onRemove={onDeleteBillingCode}
						ref={tableRef}
						remove={promptArchiveBillingCodeRow}
						rowClassName={rowClassName}
					/>
				</CustomModal.Body>
				<CustomModal.Footer>
					<Button label="Cancel" onClick={promptCancel} style="secondary" />
					<Button
						disabled={!!formState.errors.billingCodes?.length}
						label="Save"
						onClick={onSubmitBillingCodes}
						style="primary"
					/>
				</CustomModal.Footer>
			</CustomModal>

			<BillingCodeEditModeModal
				billingCode={selectedBillingCodeForEditMode}
				onClose={closeEditModeModal}
				onSubmit={submitEditMode}
				showModal={showModal && rowPendingEditMode !== null}
			/>

			<BillingCodeArchiveModal
				billingCode={selectedBillingCodeForArchive}
				onClose={closeArchiveModal}
				onConfirm={confirmArchiveBillingCode}
				showModal={showModal && rowPendingArchive !== null}
			/>

			<ConfirmationModal
				body="Canceling without saving will discard all changes made to the Billing Codes table. Do you want to continue?"
				closeModal={closeDiscardModal}
				closeText="Cancel"
				confirmAction={discardAndExit}
				confirmText="Discard"
				modalStyle="danger"
				showModal={showDiscardModalData}
				title="Discard Billing Codes Changes"
			/>
		</>
	);
};

function mapDispatchToProps() {
	return {
		getGroups: BillingCodeActions.getGroups,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(EditBillingCodesModal);
